<template lang="pug">
  b-form.user-create-edit-form(@submit.stop.prevent="onSubmit" novalidate)
    b-row(cols="1" cols-md="2")
      b-col
        b-form-group(
          label="Nombre"
          label-for="name-input"
        )
          b-form-input(
            v-model="form.name"
            :state="$v.$dirty ? !$v.form.name.$invalid : null"
            id="name-input"
          )
          div(v-if="$v.$dirty")
            b-form-invalid-feedback(:state="$v.form.name.required") Este campo es requerido
      b-col
        b-form-group(
          label="Apellido"
          label-for="last-name-input"
        )
          b-form-input(
            v-model="form.lastName"
            id="last-name-input"
          )
      b-col
        b-form-group(
          label="Email"
          label-for="email-input"
        )
          b-form-input(
            v-model="form.email"
            :state="$v.$dirty ? !$v.form.email.$invalid && !emailAlreadyExists : null"
            id="email-input"
            type="email"
          )
          div(v-if="$v.$dirty")
            b-form-invalid-feedback(:state="$v.form.email.required") Este campo es requerido
            b-form-invalid-feedback(:state="$v.form.email.email") Debe ser un email válido
            b-form-invalid-feedback(:state="!emailAlreadyExists") El email ya existe
      b-col
        b-form-group(
          label="Rol"
          label-for="role-input"
        )
          b-form-select(
            v-model="form.role"
            :state="$v.$dirty ? !$v.form.role.$invalid : null"
            id="role-input"
            :options="getRoleOptions()"
          )
          div(v-if="$v.$dirty")
            b-form-invalid-feedback(:state="$v.form.role.required") Este campo es requerido
      b-col
        b-form-group(
          :label="editMode ? 'Nueva contraseña (opcional)' : 'Contraseña' "
          label-for="password-input"
        )
          b-form-input(
            v-model="form.password"
            :state="$v.$dirty ? !$v.form.password.$invalid : null"
            id="password-input"
            type="password"
          )
          div(v-if="$v.$dirty")
            b-form-invalid-feedback(:state="$v.form.password.required") Este campo es requerido
            b-form-invalid-feedback(:state="$v.form.password.minLength") 
              | La contraseña debe tener {{$v.form.password.$params.minLength.min}} o más caracteres
      b-col
        b-form-group(
          label="Repite la contraseña"
          label-for="repeat-password-input"
        )
          b-form-input(
            v-model="form.repeatPassword"
            :state="$v.$dirty ? !$v.form.repeatPassword.$invalid : null"
            id="repeat-password-input"
            type="password"
          )
          div(v-if="$v.$dirty")
            b-form-invalid-feedback(:state="$v.form.repeatPassword.sameAsPassword") La contraseña no coinciden
    b-form-group(
      label="Grupos",
      label-for="tags-component-select"
    )
      b-form-tags.p-0.border-0(
        id="tags-component-select"
        v-model="form.businessGroups"
        size="lg"
        class="mb-2"
        add-on-change
        no-outer-focus
      )
        template(v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }")
          b-form-select(
            v-bind="inputAttrs"
            v-on="inputHandlers"
            :disabled="disabled || businessGroupsOptions.length === 0"
            :options="businessGroupsOptions"
          )
            template(v-slot:first)
              option(disabled value="") Selecciona un grupo
          ul.mt-2(v-if="tags.length > 0" class="list-inline d-inline-block mb-2")
            li(v-for="tag in tags" :key="tag" class="list-inline-item")
              b-form-tag.boton(@remove="removeTag(tag)" :title="tag" :disabled="disabled") {{  JSON.parse(tag).name }}

    b-form-checkbox(v-if="form.role === 'collaborator'" v-model="form.allowCalendarView") Permitir ver calendario
    .modal-footer
      b-btn(@click="$emit('cancel')" variant="outline-muted") Cancelar
      b-btn.btn-create.boton(type="submit") {{editMode?'Editar':'Crear'}}
</template>

<script>
import { required, email, minLength, sameAs	} from 'vuelidate/lib/validators'
import { mapActions, mapGetters } from 'vuex'
import userRoles from '../../shared/user_roles.json'

export default {
  props: {
    editMode: Boolean,
    user: Object
  },
  data () {
    return {
      form: {
        name: '',
        lastName: '',
        email: '',
        role: userRoles[0].role,
        password: '',
        repeatPassword: '',
        allowCalendarView: false,
        businessGroups: []
      },
      emailAlreadyExists: false
    }
  },
  validations () {
    let validations = {
      form: {
        name: {
          required
        },
        email: {
          required,
          email
        },
        role: {
          required
        },
        password: { 
          minLength: minLength(6)
        },
        repeatPassword: {
          sameAsPassword: sameAs('password')
        }
      }
    }
    if (!this.editMode) {
      // Validacion de contraseña al crear usuario
      validations.form.password = { 
        minLength: minLength(6),
        required
      }
    }
    return validations
  },
  methods: {
    ...mapActions('UserStore', ['createUser', 'updateUser']),
    ...mapActions(['listBusinessGroups']),
    async onSubmit () {
      
      if (this.$v.$invalid) {
        this.$v.$touch()
        return
      }
      let payload = { ...this.form }
      if (payload.password === '') {
        delete payload.password
      }
      payload.businessGroups = payload.businessGroups.map(bg => JSON.parse(bg))
      
      if (this.editMode){
        // Edita usuario existente
        const response = await this.updateUser(payload)
        if (!response) { 
          // Error al editar usuario
          this.emailAlreadyExists = true
          this.$v.$touch()
        } else {
          // Usuario editado correctamente
          this.emailAlreadyExists = false
          this.$v.$reset()
          this.$emit('success', 'El usuario ha sido editado exitosamente')
        }
      } else {
        // Crea un nuevo usuario
        const response = await this.createUser(payload)
        if (response.status >= 400) { 
          // Error al crear usuario
          if (response.data.detail.email) {
            this.emailAlreadyExists = true
            this.$v.$touch()
          } else {  
            this.$root.$bvToast.toast(response.data.message, {
              title: 'Oops!',
              variant: 'danger'
            })
          }
        } else {
          // Usuario creado correctamente
          this.emailAlreadyExists = false
          this.$v.$reset()
          this.$emit('success', 'El usuario ha sido creado exitosamente')
        }
      }
    },
    getRoleOptions () {
      const roleOptions = []
      this.roles.forEach(option => {
        const { role: value, name: text } = option
        roleOptions.push({ value, text})
      })
      return roleOptions
    }
  },
  computed: {
    ...mapGetters(['businessGroups']),
    roles () {
      return userRoles.reduce((acum, role) => 
        role.role !== 'client' ? acum.concat(role) : acum, [])
    },
    businessGroupsOptions () {
      const groupIds = this.form.businessGroups.map(group => JSON.parse(group).id)
      const filteredGroups = this.businessGroups.filter(group => !groupIds.includes(group.id))
      return filteredGroups.map(group => {
        const { id, name } = group
        return { value: JSON.stringify({ id, name }), text: name }
      })
    },
  },
  created () {
    if (this.businessGroups.length === 0) {
      this.listBusinessGroups()
    }
    if (this.editMode) {
      this.form = {
        password: '',
        repeatPassword: '',
        ...this.user
      }
      this.form.businessGroups = this.form.businessGroups.map(group => JSON.stringify(group))
    }
  }
}
</script>
